import { useState } from "react";

import { createFileRoute } from "@tanstack/react-router";

import { ArrowUpDown, Clock, Plus } from "lucide-react";

import type { PipelineStats, Prospect, ProspectStage } from "../api/hooks";
import {
  prospectStages,
  useCreateProspect,
  usePipelineStats,
  useProspects,
} from "../api/hooks";
import { nextActionLabel } from "../lib/prospect";
import { sinceLabel } from "../lib/relativeTime";
import { useDebouncedValue } from "../lib/useDebouncedValue";

import { OrganisationForm } from "./-OrganisationForm";
import { ProspectPanel } from "./-ProspectPanel";

import styles from "./pipeline.module.scss";

// The stages an operator works through, in pipeline order, with the words the
// page shows. The machine values are the API's; these are only for reading.
const stageLabels: Record<ProspectStage, string> = {
  found: "Found",
  to_contact: "To contact",
  contacted: "Contacted",
  in_conversation: "In conversation",
  demo_booked: "Demo booked",
  signed_up: "Signed up",
  lost: "Lost",
  dismissed: "Dismissed",
};

// A class per stage rather than a hashed colour, so the pipeline reads as an
// order: the grey ones are not started, the blues and purples are in flight,
// green is won and the muted ones are closed.
const stageClasses: Record<ProspectStage, string> = {
  found: styles.stageQuiet,
  to_contact: styles.stageQuiet,
  contacted: styles.stageBlue,
  in_conversation: styles.stageIndigo,
  demo_booked: styles.stagePurple,
  signed_up: styles.stageGreen,
  lost: styles.stageMuted,
  dismissed: styles.stageMuted,
};

// What a size means when nobody has recorded one. A dash, not a zero: zero
// hectares is a claim nobody has made.
function hectaresLabel(hectares: number | null): string {
  if (hectares === null) return "-";

  // Under a hectare still rounds to a number worth showing rather than "0 ha",
  // which reads as no land.
  if (hectares > 0 && hectares < 1) return "<1 ha";

  return `${Math.round(hectares).toLocaleString("en-GB")} ha`;
}

function fitWidth(fitScore: number | null): string {
  if (fitScore === null) return "0%";

  // The score is 1 to 5 and the bar is the whole width at 5.
  return `${(fitScore / 5) * 100}%`;
}

function lastTouchLabel(prospect: Prospect): string {
  if (prospect.lastInteraction === null) return "Not yet";

  const channel = prospect.lastInteraction.channel;
  const label = channel.charAt(0).toUpperCase() + channel.slice(1);

  return `${label} · ${sinceLabel(prospect.lastInteraction.occurredAt)}`;
}

function ProspectRow({
  prospect,
  onOpen,
}: {
  prospect: Prospect;
  onOpen: () => void;
}) {
  return (
    <tr>
      <td>
        {/* The name is the control, rather than the whole row: a row-wide
            click target swallows text selection, and a cell an operator wants
            to read is not a button. */}
        <button type="button" className={styles.rowOpen} onClick={onOpen}>
          <span className="tbl-org">{prospect.organisationName}</span>
        </button>
        {prospect.organisationKind !== "" && (
          <span className={styles.rowKind}>{prospect.organisationKind}</span>
        )}
      </td>
      <td>{prospect.organisationCounty || "-"}</td>
      <td className="tbl-right">{hectaresLabel(prospect.organisationHectares)}</td>
      <td>
        {/* The number and the bar together: a bar alone cannot be read off
            precisely, and a number alone cannot be scanned down a column. */}
        {prospect.fitScore === null ? (
          <span className={styles.fitUnjudged}>-</span>
        ) : (
          <span className={styles.fit}>
            <span className={styles.fitScore}>{prospect.fitScore}</span>
            <span className={styles.fitTrack} aria-hidden="true">
              <span
                className={styles.fitFill}
                style={{ width: fitWidth(prospect.fitScore) }}
              />
            </span>
          </span>
        )}
      </td>
      <td>
        <span className={`${styles.stage} ${stageClasses[prospect.stage]}`}>
          {stageLabels[prospect.stage]}
        </span>
      </td>
      <td className={styles.lastTouch}>{lastTouchLabel(prospect)}</td>
      <td className={prospect.overdue ? styles.overdue : undefined}>
        {nextActionLabel(prospect)}
      </td>
    </tr>
  );
}

function StatCards({
  stats,
  failed,
}: {
  stats: PipelineStats | undefined;
  failed: boolean;
}) {
  // Everyone contacted, which is not everyone open: found and to-contact are by
  // definition people nobody has spoken to, and counting them would make the
  // conversion rate fall every time the scout finds something.
  //
  // Named as the two that are out rather than the six that are in, so a stage
  // added later is in the denominator until someone says otherwise. A stage
  // list maintained by hand here is the copy the data layer's own comment warns
  // about: the pill would appear and the denominator would silently lose it.
  //
  // Dismissed is counted, though some of them were dismissed before anyone
  // spoke to them. The data cannot tell those apart, and this is the direction
  // that understates: leaving it out lets "nine dismissed after a conversation,
  // one signed up" print 100%.
  //
  // A percentage of nothing is not zero per cent, it is nothing to say.
  const uncontactedStages: ProspectStage[] = ["found", "to_contact"];
  const contacted = stats
    ? prospectStages
        .filter((name) => !uncontactedStages.includes(name))
        .reduce((total, name) => total + (stats.stageCounts[name] ?? 0), 0)
    : 0;
  const signedUpShare =
    stats && contacted > 0
      ? `${Math.round((stats.signedUpCount / contacted) * 100)}% of everyone contacted`
      : "Nobody contacted yet";

  return (
    <div className="stats-row stats-row-3">
      <div className="stat-card">
        <div className="stat-label">Overdue</div>
        <div className="stat-value">{stats ? stats.overdueCount : "-"}</div>
        <div className="stat-sub">
          {/* A failed fetch must not read as an empty pipeline. */}
          {failed ? "The numbers could not be loaded" : "Follow-ups past their date"}
        </div>
      </div>
      <div className="stat-card">
        <div className="stat-label">In outreach</div>
        <div className="stat-value">{stats ? stats.openCount : "-"}</div>
        <div className="stat-sub">
          {failed
            ? "The numbers could not be loaded"
            : "Still being worked, at any stage"}
        </div>
      </div>
      <div className="stat-card">
        <div className="stat-label">Signed up</div>
        <div className="stat-value">{stats ? stats.signedUpCount : "-"}</div>
        <div className="stat-sub">
          {failed ? "The numbers could not be loaded" : signedUpShare}
        </div>
      </div>
    </div>
  );
}

function Pipeline() {
  const [search, setSearch] = useState("");
  const [stage, setStage] = useState<ProspectStage | undefined>(undefined);
  const [dueNow, setDueNow] = useState(false);
  // Held at the route rather than in the row: the list sits in a .panel, whose
  // backdrop-filter makes it the containing block for anything fixed inside
  // it, so a slide-over rendered from a row would be sized against the table.
  const [opened, setOpened] = useState<Prospect | null>(null);
  const [adding, setAdding] = useState(false);
  const createProspect = useCreateProspect();

  const searchTerm = useDebouncedValue(search.trim(), 250);

  const filter = {
    stages: stage ? [stage] : undefined,
    search: searchTerm === "" ? undefined : searchTerm,
    dueNow,
  };

  const {
    data,
    isPending,
    isError,
    isPlaceholderData,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
  } = useProspects(filter);

  const { data: stats, isError: statsFailed } = usePipelineStats(filter);

  const prospects = data?.pages.flatMap((page) => page.items) ?? [];
  const total = data?.pages[0]?.count;

  // Every stage is drawn, whether or not anything is in it: a pill that
  // disappears when its column empties is a column an operator forgets exists.
  //
  // The count is undefined rather than zero when the stats have not arrived: a
  // row of zeros beside a table of real rows reads as an empty pipeline, which
  // is the misread the cards are already guarded against.
  const stagePills = prospectStages.map((value) => ({
    value,
    label: stageLabels[value],
    count: stats === undefined ? undefined : (stats.stageCounts[value] ?? 0),
  }));

  return (
    <>
      <div className={styles.pageHead}>
        <div className="page-header">
          <div className="page-title">Pipeline</div>
          <div className="page-subtitle">Who to contact next, soonest first</div>
        </div>

        {/* An organisation is added here as well as on the organisations page,
            because this is where someone is when they think of one. The record
            and the prospect are written together: a body added from the
            pipeline that is not in the pipeline is not what was asked for. */}
        <button
          type="button"
          className={styles.add}
          onClick={() => setAdding(true)}
        >
          <Plus size={14} />
          Add organisation
        </button>
      </div>

      <StatCards stats={stats} failed={statsFailed} />

      <div className="panel" aria-busy={isPlaceholderData}>
        <div className={styles.head}>
          <span className={styles.headTitle}>Outreach pipeline</span>
          {total !== undefined && (
            <span className={styles.headCount}>
              {total.toLocaleString("en-GB")}{" "}
              {total === 1 ? "organisation" : "organisations"}
            </span>
          )}

          <div className={styles.headRight}>
            <input
              type="search"
              className={styles.search}
              placeholder="Search organisations"
              aria-label="Search organisations"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
            />
            <button
              type="button"
              className={styles.toggle}
              aria-pressed={dueNow}
              onClick={() => setDueNow(!dueNow)}
            >
              <Clock size={13} />
              Due now only
            </button>
            {/* Stated rather than offered: the list has one order, and saying
                so is what stops an operator looking for the control that
                changes it. */}
            <span className={styles.sort}>
              <ArrowUpDown size={13} />
              Next action
            </span>
          </div>
        </div>

        <div className={styles.stages} role="group" aria-label="Filter by stage">
          <span className={styles.stagesLabel}>Stage</span>
          <button
            type="button"
            className={styles.stagePill}
            aria-pressed={stage === undefined}
            onClick={() => setStage(undefined)}
          >
            All
          </button>
          {stagePills.map((pill) => (
            <button
              key={pill.value}
              type="button"
              className={styles.stagePill}
              aria-pressed={stage === pill.value}
              onClick={() => setStage(pill.value)}
            >
              {pill.label}
              <span className={styles.stagePillCount}>
                {pill.count === undefined ? "-" : pill.count}
              </span>
            </button>
          ))}
        </div>

        {isPending && (
          <div className={styles.state} role="status">
            Loading the pipeline…
          </div>
        )}

        {isError && (
          <div className={styles.state} role="alert">
            The pipeline could not be loaded.
          </div>
        )}

        {!isPending && !isError && prospects.length === 0 && (
          <div className={styles.state} role="status">
            {searchTerm === "" && stage === undefined && !dueNow
              ? "Nothing in the pipeline yet."
              : "Nothing matches that."}
          </div>
        )}

        {prospects.length > 0 && (
          <table className="tbl" aria-label="Pipeline">
            <thead>
              <tr>
                <th>Organisation</th>
                <th>County</th>
                <th className="tbl-right">Size</th>
                <th>Fit</th>
                <th>Stage</th>
                <th>Last touch</th>
                <th>Next action</th>
              </tr>
            </thead>
            <tbody>
              {prospects.map((prospect) => (
                <ProspectRow
                  key={prospect.id}
                  prospect={prospect}
                  onOpen={() => setOpened(prospect)}
                />
              ))}
            </tbody>
          </table>
        )}

        {hasNextPage && (
          <div className={styles.foot}>
            {total !== undefined && (
              <span className={styles.footNote}>
                {prospects.length.toLocaleString("en-GB")} of{" "}
                {total.toLocaleString("en-GB")} shown
              </span>
            )}
            <button
              type="button"
              className={styles.loadMore}
              disabled={isFetchingNextPage}
              onClick={() => fetchNextPage()}
            >
              {isFetchingNextPage ? "Loading…" : "Load more"}
            </button>
          </div>
        )}
      </div>
      {adding && (
        <OrganisationForm
          record={null}
          title="Add to the pipeline"
          onClose={() => setAdding(false)}
          onCreated={(created) =>
            createProspect.mutateAsync({
              organisationId: created.id,
              // Where an organisation added by hand starts: someone typed it
              // in, so it is past being found and waiting to be contacted.
              stage: "to_contact",
            })
          }
          onCreatedFailureMessage={(created) =>
            `${created.name} was added, but putting it in the pipeline failed. Try again, or find it in the organisations list.`
          }
        />
      )}

      {opened !== null && (
        <ProspectPanel
          key={opened.id}
          // The row as it is now, so a move made in the panel and refetched
          // into the list is what the panel redraws from.
          prospect={prospects.find((row) => row.id === opened.id) ?? opened}
          stageLabels={stageLabels}
          onClose={() => setOpened(null)}
        />
      )}
    </>
  );
}

export const Route = createFileRoute("/pipeline")({
  component: Pipeline,
});
